import type { IDependentControl } from '~/presentation/reports/common/type';
import type { CheckboxGroupType, SelectOptionType } from '../../type';

import { EDependentControlAction } from '~/presentation/reports/common/type';
import { useCallback, useMemo } from 'react';

interface UseDependentDisableOptionProps {
  dependentControls?: IDependentControl[];
  optionsGroup?: CheckboxGroupType;
}

interface IDependentDisableOptionReturn {
  optionsGroup?: CheckboxGroupType;
  dependentDisabledValues: Set<string>;
}

export const useDependentDisableOption = ({
  dependentControls,
  optionsGroup,
}: UseDependentDisableOptionProps) => {
  const getDisableState = useCallback(
    (option: SelectOptionType) => {
      if (!dependentControls?.length || !option.dependentControls?.length) {
        return {
          isDisabled: option.disabled,
          isDisableByDependent: false,
        };
      }

      const disableDependencies = option.dependentControls.filter(
        (control) => control.action === EDependentControlAction.DISABLE,
      );

      if (!disableDependencies.length) {
        return {
          isDisabled: !!option.disabled,
          isDisableByDependent: false,
        };
      }

      const isDisableByDependent = disableDependencies.some((dependency) => {
        const matchingControl = dependentControls.find((control) => control.id === dependency.id);
        return !matchingControl?.value;
      });

      return {
        isDisabled: option.disabled || isDisableByDependent,
        isDisableByDependent,
      };
    },
    [dependentControls],
  );

  const result = useMemo((): IDependentDisableOptionReturn => {
    const dependentDisabledValues = new Set<string>();

    const processedGroups = optionsGroup?.map((group) => ({
      ...group,
      options: group.options.map((option) => {
        const { isDisabled, isDisableByDependent } = getDisableState(option);

        if (isDisableByDependent) {
          dependentDisabledValues.add(option.value);
        }

        return {
          ...option,
          disabled: isDisabled,
        };
      }),
    }));

    return {
      optionsGroup: processedGroups,
      dependentDisabledValues: dependentDisabledValues,
    };
  }, [optionsGroup, getDisableState]);
  return result;
};
